import { useMemo } from 'react';
import { ParameterDto, RegulationDto, SampleFormat } from '@src/types/types.ts';

import { DEFAULT_PARAMETER_BAR_CLOSE_TO_LIMIT_THRESHOLD } from '@src/constants.ts';
import { ParameterStatusType } from '@components/atoms/ParameterStatusType.ts';

export const useParameterStatus = (
  parameter: ParameterDto,
  format: string,
  numericValue: number,
  regulations: RegulationDto[] | undefined,
) => {
  const formatEnum = stringToSampleFormat(format);
  return useMemo(() => {
    if (parameter.notAnalyzable) {
      return {
        exceededRegulation: undefined,
        status: ParameterStatusType.UNSET,
      };
    }
    if (formatEnum !== SampleFormat.NUMERIC || !regulations?.length) {
      return {
        exceededRegulation: undefined,
        status: ParameterStatusType.UNSET,
      };
    }
    if (
      regulations.length === 1 &&
      !regulations[0].limit &&
      !regulations[0].lowerLimit
    ) {
      return {
        exceededRegulation: regulations[0],
        status: ParameterStatusType.UNSET,
      };
    }
    if (regulations.some((regulation) => regulation.lowerLimit)) {
      return {
        exceededRegulation: regulations.find(
          (regulation) =>
            (!regulation.lowerLimit || numericValue > regulation.lowerLimit) &&
            (!regulation.limit || numericValue <= regulation.limit),
        ),
        status: ParameterStatusType.UNSET,
      };
    }
    const sortedRegulations = regulations.sort(
      (a, b) => (b.limit ?? 0) - (a.limit ?? 0),
    );
    const exceededRegulation = sortedRegulations.find(
      (regulation) => regulation.limit && numericValue > regulation.limit,
    );
    if (exceededRegulation) {
      return {
        exceededRegulation,
        status: ParameterStatusType.ERROR,
      };
    }
    const lowestLimit = sortedRegulations[sortedRegulations.length - 1].limit;
    const limitTolerance =
      sortedRegulations[sortedRegulations.length - 1].closeToLimitTolerance ??
      DEFAULT_PARAMETER_BAR_CLOSE_TO_LIMIT_THRESHOLD;
    if (lowestLimit && numericValue > lowestLimit * (1 - limitTolerance)) {
      return {
        exceededRegulation: sortedRegulations[sortedRegulations.length - 1],
        status: ParameterStatusType.WARNING,
      };
    }
    return {
      exceededRegulation: undefined,
      status: ParameterStatusType.SUCCESS,
    };
  }, [parameter.notAnalyzable, formatEnum, numericValue, regulations]);
};

const stringToSampleFormat = (value: string): SampleFormat | undefined => {
  if (Object.values(SampleFormat).includes(value as SampleFormat)) {
    return value as SampleFormat;
  }
  return undefined;
};
